import { ArrowLeft } from 'lucide-react';
import { Fragment, useMemo } from 'react';
import { Link } from 'react-router-dom';
import ManualLink from '../../../components/ManualLink';
import { useAuth } from '../../../context/AuthContext';
import {
  type StoryAnalysisLike,
  selectLatestAnalysis,
  selectLatestSentimentNarrative,
} from '../../../helpers/postScoreAndMetrics.helpers';
import { useStoryScoreUpdates } from '../../../hooks/useStoryScoreUpdates';
import type { AnalysisResponse } from '../../../interfaces/analysisResponse';
import CompositionRadar, {
  type CompositionRow,
} from './charts/CompositionRadar';
import EmotionPolar from './charts/EmotionPolar';
import ScoreCompositionDiagram from './charts/ScoreCompositionDiagram';
import ContentsIndex from './sections/ContentsIndex';
import EmotionalRead from './sections/EmotionalRead';
import FindingsCallouts from './sections/FindingsCallouts';
import InsightsPanel from './sections/InsightsPanel';
import IntegrityPills, { type IntegrityFlag } from './sections/IntegrityPills';
import PerformancePanel from './sections/PerformancePanel';
import QualityMeters from './sections/QualityMeters';
import ScoreHero from './sections/ScoreHero';
import SectionShell, { type SectionDef } from './sections/SectionShell';
import SentimentBar from './sections/SentimentBar';
import SentimentDrivers from './sections/SentimentDrivers';
import SentimentNarrative from './sections/SentimentNarrative';
import TopicBars from './sections/TopicBars';
import './post-analysis.scss';

interface Props {
  /** Post or reply id — drives the live-score subscription + score-map filter. */
  storyId: string;
  /** Raw storyAnalysis block from getPostById / getReplyById. */
  storyAnalysis: StoryAnalysisLike | null | undefined;
  /** Persisted score fallback when there's no live update for this id. */
  baseScore: number | null;
  replyCount: number;
  /** Author of the analyzed entity — gates the author-only coaching section. */
  authorId: string | null | undefined;
  backTo: string;
  backLabel: string;
  /** Empty-state copy, e.g. "This post hasn't been analyzed yet." */
  notAnalyzedLabel: string;
  /**
   * True only for the per-post page; gates the post-only Performance/Insights
   * sections. Replies are not posts.
   */
  isPost?: boolean;
}

/**
 * TRIL-367: the Story Analysis dashboard body, shared by the per-post
 * (TRIL-263) and per-reply analysis pages. Each route is a thin fetch-wrapper
 * that resolves the entity and hands the already-fetched data here; this
 * component owns the live score, the section registry, and the render.
 */
export default function AnalysisDashboard({
  storyId,
  storyAnalysis,
  baseScore,
  replyCount,
  authorId,
  backTo,
  backLabel,
  notAnalyzedLabel,
  isPost = false,
}: Props) {
  const { latestUpdate } = useStoryScoreUpdates({ storyId });
  const { user } = useAuth();

  const analysis = selectLatestAnalysis<AnalysisResponse>(
    storyAnalysis as StoryAnalysisLike,
  );
  // The explanatory narrative lives on `analysis[].sentiment` (type
  // SentimentResponse) — a SIBLING of `storyAnalyzer`, not inside
  // plugin_results. Pull it from the same latest-with-data version that
  // selectLatestAnalysis settled on, so bar and narrative never drift.
  const sentimentNarrative = selectLatestSentimentNarrative(
    storyAnalysis as StoryAnalysisLike,
  );
  // Only trust the live update if it's for THIS entity (the score map is global).
  const liveScore =
    (latestUpdate && latestUpdate.storyId === storyId
      ? latestUpdate.newScore
      : null) ??
    baseScore ??
    null;

  // Memoized so the array identity is stable across live-score re-renders.
  // ScoreCompositionDiagram's mermaid effect depends on `rows`; a fresh array
  // each render would re-fire it needlessly on every WebSocket score update.
  const pr = analysis?.plugin_results;
  const compositionRows = useMemo<CompositionRow[]>(
    () => [
      {
        label: 'Sentiment',
        value: Math.round(pr?.sentiment?.contribution_to_overall ?? 0),
      },
      {
        label: 'Readability',
        value: Math.round(
          pr?.advanced_readability?.contribution_to_overall ?? 0,
        ),
      },
      {
        label: 'Quality',
        value: Math.round(pr?.quality_metrics?.contribution_to_overall ?? 0),
      },
      {
        label: 'Topic',
        value: Math.round(pr?.topic_relevance?.contribution_to_overall ?? 0),
      },
      {
        label: 'AI',
        value: Math.round(pr?.ai_detection?.contribution_to_overall ?? 0),
      },
      {
        label: 'Originality',
        value: Math.round(
          pr?.plagiarism_detection?.contribution_to_overall ?? 0,
        ),
      },
    ],
    [pr],
  );

  if (!analysis || !pr) {
    return (
      <div className="POST__ANALYSIS POST__ANALYSIS--state">
        <Link to={backTo} className="POST__ANALYSIS__back">
          <ArrowLeft size={16} /> {backLabel}
        </Link>
        <p className="POST__ANALYSIS__empty">{notAnalyzedLabel}</p>
      </div>
    );
  }

  const meters = [
    pr.advanced_readability && {
      label: 'Reading ease',
      value: pr.advanced_readability.flesch_reading_ease,
      max: 100,
      hint: pr.advanced_readability.reading_level,
    },
    pr.quality_metrics && {
      label: 'Grammar',
      value: pr.quality_metrics.grammar_score,
      max: 100,
    },
    pr.quality_metrics && {
      label: 'Readability',
      value: pr.quality_metrics.readability_score,
      max: 100,
    },
    pr.quality_metrics && {
      label: 'Words',
      value: pr.quality_metrics.word_count,
      max: Math.max(pr.quality_metrics.word_count, 1000),
    },
  ].filter(Boolean) as Array<{
    label: string;
    value: number;
    max: number;
    hint?: string;
  }>;

  const integrity: IntegrityFlag[] = [
    { label: 'AI-generated', tripped: !!pr.ai_detection?.is_likely_ai },
    {
      label: 'Manipulative tone',
      tripped: !!pr.emotional_manipulation?.is_manipulative,
    },
    {
      label: 'Needs fact-checking',
      tripped: !!pr.misinformation_detection?.needs_fact_checking,
    },
    {
      label: 'Original',
      tripped: pr.plagiarism_detection
        ? !pr.plagiarism_detection.is_likely_original
        : false,
    },
    { label: 'Spam', tripped: !!pr.spam_detection?.is_likely_spam },
  ];

  const findingsList = analysis.interpretations?.key_findings ?? [];
  const recsList = analysis.interpretations?.recommendations?.length
    ? analysis.interpretations.recommendations
    : (analysis.user_recommendations ?? []);

  // Findings & recommendations are author-directed coaching — show them only to
  // the entity's author, never to readers (TRIL-293).
  const isAuthor = !!user?.id && user.id === authorId;

  // ── Section registry — the SINGLE SOURCE OF TRUTH for the dashboard. ──────
  // Adding a section + Contents-menu item later = add ONE entry here (plus its
  // content component). The array order drives (a) the Contents index, (b) the
  // render order, and (c) the auto-numbered eyebrows. Entries whose data is
  // absent are filtered out, so the menu and the page can never drift apart.
  const defs: SectionDef[] = [
    {
      id: 'score',
      label: 'Quality',
      eyebrow: 'Overall quality',
      render: () => (
        <ScoreHero
          score={liveScore ?? analysis.overall_score}
          replyCount={replyCount}
          delta={latestUpdate?.scoreDelta ?? null}
        />
      ),
    },
    pr.sentiment && {
      id: 'sentiment',
      label: 'Tone',
      eyebrow: 'Tone',
      render: () => (
        <>
          <SentimentBar
            primary={pr.sentiment!.primary_sentiment}
            confidence={pr.sentiment!.confidence}
            scores={pr.sentiment!.scores}
          />
          <SentimentDrivers
            emotionScores={pr.emotion_detection?.emotion_scores}
          />
          <SentimentNarrative
            summary={sentimentNarrative?.sentiment_summary}
            confidenceExplanation={sentimentNarrative?.confidence_explanation}
            contributionExplanation={
              sentimentNarrative?.contribution_explanation
            }
          />
        </>
      ),
    },
    pr.emotion_detection?.emotion_scores && {
      id: 'emotions',
      label: 'Emotions',
      eyebrow: 'Emotions',
      render: () => (
        <EmotionPolar scores={pr.emotion_detection!.emotion_scores} />
      ),
    },
    pr.topic_relevance && {
      id: 'topic',
      label: 'Topic',
      eyebrow: 'Topic relevance',
      render: () => (
        <TopicBars
          primaryTopic={pr.topic_relevance!.primary_topic}
          isControversial={pr.topic_relevance!.is_controversial}
          categoryScores={{ ...pr.topic_relevance!.category_scores }}
        />
      ),
    },
    meters.length > 0 && {
      id: 'quality',
      label: 'Quality',
      eyebrow: 'Readability & quality',
      render: () => <QualityMeters meters={meters} />,
    },
    {
      id: 'integrity',
      label: 'Integrity',
      eyebrow: 'Integrity',
      render: () => <IntegrityPills flags={integrity} />,
    },
    {
      id: 'composition',
      label: 'Composition',
      eyebrow: 'Score composition',
      render: () => (
        <>
          <CompositionRadar rows={compositionRows} />
          <ScoreCompositionDiagram rows={compositionRows} />
        </>
      ),
    },
    isAuthor &&
      (findingsList.length > 0 || recsList.length > 0) && {
        id: 'findings',
        label: 'For you',
        eyebrow: 'Findings & recommendations',
        render: () => (
          <>
            <p className="POST__ANALYSIS__private-note">
              Visible only to you — your private writing coach for this piece.
            </p>
            <FindingsCallouts
              keyFindings={findingsList}
              recommendations={recsList}
            />
          </>
        ),
      },
    isAuthor &&
      isPost && {
        id: 'performance',
        label: 'Performance',
        eyebrow: 'How it did',
        render: () => <PerformancePanel postId={storyId} />,
      },
    isAuthor &&
      isPost && {
        id: 'insights',
        label: 'Insights',
        eyebrow: 'What it means',
        render: () => <InsightsPanel postId={storyId} />,
      },
  ].filter(Boolean) as SectionDef[];

  return (
    <div className="POST__ANALYSIS">
      <div className="POST__ANALYSIS__topbar">
        <Link to={backTo} className="POST__ANALYSIS__back">
          <ArrowLeft size={16} /> {backLabel}
        </Link>
        <ManualLink section="story-analysis" label="How this is measured" />
      </div>
      <div className="POST__ANALYSIS__grid">
        <ContentsIndex
          sections={defs.map(({ id, label }) => ({ id, label }))}
        />
        <main className="POST__ANALYSIS__main">
          {defs.map((d, i) => (
            <Fragment key={d.id}>
              <SectionShell id={d.id} num={i + 1} eyebrow={d.eyebrow}>
                {d.render()}
              </SectionShell>
              {/* Verdict-first lead band: sits right after the score hero,
                  before the numbered detail sections (TRIL-286). */}
              {d.id === 'score' && (
                <EmotionalRead
                  emotion={pr.emotion_detection}
                  storySummary={pr.story_summary}
                />
              )}
            </Fragment>
          ))}
        </main>
      </div>
    </div>
  );
}
